/* eslint-disable @typescript-eslint/no-explicit-any */
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import { getDeliveryWindowText } from 'utils/transformations/deliveryWindows'
import { relayConnectionToArray } from 'utils/transformations/graphql'
import { getCollectionNames } from 'utils/transformations/orders'

const getSummaryData = (order: any) => {
  const { lineItems, discountAmount, shippingPrice, taxAmount } = order
  if (!lineItems) {
    return {}
  }

  const {
    totalUnits: quantity,
    totalPrice: subtotal,
    discountedPrice,
  } = lineItems

  return {
    quantity: parseInt(quantity, 10),
    subtotal: parseFloat(subtotal || 0),
    discountedPrice: parseFloat(discountedPrice || 0),
    discount: discountAmount || 0,
    shipping: shippingPrice || 0,
    taxes: taxAmount || 0,
  }
}

const getTotal = (summaryData: any) => {
  const { discountedPrice, shipping, taxes } = summaryData

  return discountedPrice + shipping + taxes
}

const getSalesRep = (order: any) => {
  if (!order.salesRepresentative) {
    return null
  }
  const { name, email } = order.salesRepresentative
  return `${name} - ${email}`
}

const getShippingAddressArray = (order: any) => {
  const shippingCompany = getSafeString(order, 'shippingCompany')
  const shippingFirstAddress = getSafeString(order, 'shippingFirstAddress')
  const shippingSecondAddress = getSafeString(order, 'shippingSecondAddress')
  const shippingCity = getSafeString(order, 'shippingCity')
  const shippingState = getSafeString(order, 'shippingState')
  const shippingZip = getSafeString(order, 'shippingZip')
  const shippingCountry = getSafeString(order, 'shippingCountry')

  return [
    shippingCompany,
    shippingFirstAddress,
    shippingSecondAddress,
    `${shippingCity}, ${shippingState} ${shippingZip}`,
    shippingCountry,
  ]
}

const getBillingAddressArray = (order: any) => {
  const billingCompany = getSafeString(order, 'billingCompany')
  const billingFirstAddress = getSafeString(order, 'billingFirstAddress')
  const billingSecondAddress = getSafeString(order, 'billingSecondAddress')
  const billingCity = getSafeString(order, 'billingCity')
  const billingState = getSafeString(order, 'billingState')
  const billingZip = getSafeString(order, 'billingZip')
  const billingCountry = getSafeString(order, 'billingCountry')

  return [
    billingCompany,
    billingFirstAddress,
    billingSecondAddress,
    `${billingCity}, ${billingState} ${billingZip}`,
    billingCountry,
  ]
}

const getDataFromCollections = (order: any) => {
  const { collections: rawCollections } = order
  const collections = relayConnectionToArray(rawCollections)
  if (!collections || !collections.length) {
    return { collectionNames: null }
  }

  return {
    collectionNames: getCollectionNames(order),
  }
}

const getSeason = (order: any) => {
  const season = get(order, 'seasonYear.season.name', '')
  const year = get(order, 'seasonYear.year', '')
  return `${season} ${year}`
}

const getSafeString = (obj: any, property: any) => {
  const val = get(obj, property)
  return val ?? ''
}

const getStreet = (order: any) => {
  const fullAddress =
    getSafeString(order, 'billingFirstAddress') +
    ' ' +
    getSafeString(order, 'billingSecondAddress')
  return fullAddress.trim()
}

const transformOrder = (order: any) => {
  if (!isEmpty(order)) {
    const summaryData = getSummaryData(order)
    return {
      ...order,
      billingAddress: getBillingAddressArray(order),
      billingAddressDictionary: {
        name: getSafeString(order, 'billingCompany'),
        email: getSafeString(order, 'shippingEmail'),
        street: getStreet(order),
        city: getSafeString(order, 'billingCity'),
        state: getSafeString(order, 'billingState'),
        country: getSafeString(order, 'billingCountry'),
        zip: getSafeString(order, 'billingZip'),
      },
      brandName: get(order, 'brand.displayName'),
      currencyCode: get(order, 'priceType.currency.code'),
      currencyDecimals: get(order, 'priceType.currency.decimals', 2),
      deliveryWindow: getDeliveryWindowText(
        order.deliveryStart,
        order.deliveryComplete,
      ),
      destinationAccountId: get(order, 'brand.id'),
      orderTotal: getTotal(summaryData),
      phoneNumber: get(order, 'shippingPhone', null),
      salesRep: getSalesRep(order),
      season: getSeason(order),
      shippingAddress: getShippingAddressArray(order),
      summaryData,
      /* 
        when the query comes from graphene, it won't have partialPaymentRequests.
        we want to make sure we don't pass down the partialPaymentRequests prop
        so we can make an extra query for that date down the comp tree
      */
      ...(order?.partialPaymentRequests && {
        partialPaymentRequests: get(order, 'partialPaymentRequests')?.[0],
      }),
      ...getDataFromCollections(order),
    }
  }
}

export default transformOrder
